import { type PropsWithChildren, useEffect, useRef, useState } from 'react'
import { PortraitPlaceholder } from './primitives'

const TILE_WIDTH = 460
const GAP = 32

function ScaledPreview({
  children,
  bg = '#fff',
}: PropsWithChildren<{ bg?: string }>) {
  const ref = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(TILE_WIDTH / 1280)

  useEffect(() => {
    if (!ref.current) return
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        setScale(e.contentRect.width / 1280)
      }
    })
    ro.observe(ref.current)
    return () => ro.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      style={{
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid var(--line)',
        background: bg,
        borderRadius: 4,
        aspectRatio: '4/3',
        width: '100%',
        transition: 'transform 300ms cubic-bezier(.2,.8,.2,1), box-shadow 300ms',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          transformOrigin: 'top left',
          width: 1280,
          height: 960,
          transform: `scale(${scale})`,
        }}
      >
        {children}
      </div>
    </div>
  )
}

function PreviewGlutenvrij() {
  return (
    <ScaledPreview bg="#fff">
      <img
        src="/previews/glutenvrijadvies.webp"
        alt="glutenvrijadvies.nl"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'top center',
          display: 'block',
        }}
      />
    </ScaledPreview>
  )
}

function PreviewBrandreel() {
  return (
    <ScaledPreview bg="#fff">
      <img
        src="/previews/brandreel.webp"
        alt="Brandreel"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'top center',
          display: 'block',
        }}
      />
    </ScaledPreview>
  )
}

export function PreviewSarah() {
  return (
    <ScaledPreview bg="#F5F1E8">
      <div
        style={{
          padding: '40px 64px',
          fontFamily: 'var(--sans)',
          color: '#1A1A14',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15 }}>
          <span style={{ fontFamily: 'Fraunces, serif', fontWeight: 500, fontSize: 22 }}>
            Sarah Chen.
          </span>
          <span style={{ display: 'flex', gap: 32, color: 'rgba(26,26,20,0.7)' }}>
            <span>Work</span>
            <span>Approach</span>
            <span>Essays</span>
            <span>Contact</span>
          </span>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.3fr 1fr',
            gap: 56,
            marginTop: 56,
            flex: 1,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 13,
                color: 'rgba(26,26,20,0.55)',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                marginBottom: 24,
              }}
            >
              Fractional CMO
            </div>
            <div
              style={{
                fontFamily: 'Fraunces, serif',
                fontSize: 84,
                lineHeight: 0.95,
                letterSpacing: '-0.03em',
                fontWeight: 300,
              }}
            >
              I run <span style={{ fontStyle: 'italic' }}>growth</span> for Series B companies
              that aren&apos;t ready for a full-time CMO.
            </div>
            <div
              style={{
                marginTop: 40,
                fontSize: 17,
                lineHeight: 1.5,
                color: 'rgba(26,26,20,0.75)',
                maxWidth: 520,
              }}
            >
              Previously VP Growth at Ramp. Three current partners, all on retainer.
            </div>
          </div>
          <div>
            <PortraitPlaceholder label="SARAH CHEN / PORTRAIT" />
            <div
              style={{
                marginTop: 24,
                fontFamily: 'var(--mono)',
                fontSize: 13,
                color: 'rgba(26,26,20,0.55)',
                lineHeight: 1.8,
              }}
            >
              <div>+ Ramp, Linear, Vercel</div>
              <div>+ SF / New York</div>
              <div>+ 1 seat, Q2 2026</div>
            </div>
          </div>
        </div>
      </div>
    </ScaledPreview>
  )
}

function PreviewMarcus() {
  return (
    <ScaledPreview bg="#0E0E0E">
      <div
        style={{
          padding: '40px 64px',
          fontFamily: 'var(--sans)',
          color: '#F5F5F0',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 14,
            fontFamily: 'var(--mono)',
            letterSpacing: '0.06em',
          }}
        >
          <span>MARCUS REID</span>
          <span style={{ color: 'rgba(245,245,240,0.5)' }}>
            FRACTIONAL COO · EST. 2012
          </span>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 0.8fr',
            gap: 64,
            marginTop: 80,
            flex: 1,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 76,
                lineHeight: 1.02,
                letterSpacing: '-0.025em',
                fontWeight: 400,
              }}
            >
              Seventeen companies scaled past $50M ARR. One playbook that ships in ninety
              days.
            </div>
            <div
              style={{
                marginTop: 56,
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 32,
                borderTop: '1px solid rgba(245,245,240,0.12)',
                paddingTop: 28,
              }}
            >
              {(
                [
                  ['$840M', 'ARR supported'],
                  ['4', 'Exits (2 IPO)'],
                  ['Q3', 'Next availability'],
                ] as const
              ).map(([k, v]) => (
                <div key={v}>
                  <div
                    style={{ fontSize: 38, fontWeight: 400, letterSpacing: '-0.02em' }}
                  >
                    {k}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      fontFamily: 'var(--mono)',
                      color: 'rgba(245,245,240,0.5)',
                      marginTop: 6,
                      letterSpacing: '0.06em',
                    }}
                  >
                    {v}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <PortraitPlaceholder label="MARCUS REID / PORTRAIT" tone="dark" />
          </div>
        </div>
        <div style={{ marginTop: 'auto', paddingTop: 24, display: 'flex', gap: 10 }}>
          <div
            style={{
              padding: '12px 20px',
              background: '#F5F5F0',
              color: '#0E0E0E',
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            Start a conversation →
          </div>
          <div
            style={{
              padding: '12px 20px',
              border: '1px solid rgba(245,245,240,0.2)',
              fontSize: 14,
            }}
          >
            Download playbook
          </div>
        </div>
      </div>
    </ScaledPreview>
  )
}

function PreviewPriya() {
  return (
    <ScaledPreview bg="#FFFFFF">
      <div
        style={{
          padding: '36px 56px',
          fontFamily: 'var(--sans)',
          color: '#0A0A0A',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 14,
            paddingBottom: 20,
            borderBottom: '1px solid rgba(10,10,10,0.1)',
          }}
        >
          <span style={{ fontWeight: 600 }}>Priya Nair, CFO</span>
          <span style={{ display: 'flex', gap: 28, color: 'rgba(10,10,10,0.6)' }}>
            <span>01 Work</span>
            <span>02 Rounds closed</span>
            <span>03 Writing</span>
            <span>04 Book intro</span>
          </span>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '0.9fr 1.3fr',
            gap: 48,
            marginTop: 48,
            flex: 1,
          }}
        >
          <div>
            <PortraitPlaceholder label="PRIYA NAIR / PORTRAIT" aspect="4/5" />
            <div
              style={{
                marginTop: 20,
                fontSize: 13,
                color: 'rgba(10,10,10,0.55)',
                fontFamily: 'var(--mono)',
                lineHeight: 1.8,
              }}
            >
              <div>Mercury · Brex · Anduril</div>
              <div>CPA, Wharton MBA</div>
              <div>Based in NYC</div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 12,
                color: 'rgba(10,10,10,0.5)',
                letterSpacing: '0.08em',
                marginBottom: 16,
              }}
            >
              FRACTIONAL CFO
            </div>
            <div
              style={{
                fontSize: 50,
                lineHeight: 1.04,
                letterSpacing: '-0.025em',
                fontWeight: 400,
              }}
            >
              I get your numbers ready before the term sheet does.
            </div>
            <div
              style={{
                marginTop: 40,
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
              }}
            >
              {(
                [
                  ['Series A & B readiness', '4 to 8 weeks'],
                  ['Board and investor reporting', 'Ongoing'],
                  ['Revenue modeling', '2 to 4 weeks'],
                  ['Cap table and 409A', 'Fixed scope'],
                ] as const
              ).map(([a, b]) => (
                <div
                  key={a}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    paddingBottom: 14,
                    borderBottom: '1px solid rgba(10,10,10,0.08)',
                  }}
                >
                  <span style={{ fontSize: 16 }}>{a}</span>
                  <span
                    style={{
                      fontSize: 13,
                      color: 'rgba(10,10,10,0.5)',
                      fontFamily: 'var(--mono)',
                    }}
                  >
                    {b}
                  </span>
                </div>
              ))}
            </div>
            <div
              style={{
                marginTop: 'auto',
                paddingTop: 24,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  color: 'rgba(10,10,10,0.55)',
                  fontStyle: 'italic',
                }}
              >
                &quot;Closed our Series A six weeks faster.&quot; Fabric Labs
              </div>
              <div
                style={{
                  fontSize: 13,
                  padding: '10px 16px',
                  background: '#0A0A0A',
                  color: '#fff',
                }}
              >
                Book intro →
              </div>
            </div>
          </div>
        </div>
      </div>
    </ScaledPreview>
  )
}

function PreviewTheo() {
  return (
    <ScaledPreview bg="#E9E4DB">
      <div
        style={{
          padding: '40px 64px',
          fontFamily: 'var(--sans)',
          color: '#1C1A15',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 20, height: 20, background: '#1C1A15', borderRadius: 2 }} />
            <span style={{ fontSize: 16, fontWeight: 500 }}>Laurent</span>
          </div>
          <div
            style={{
              fontSize: 13,
              fontFamily: 'var(--mono)',
              color: 'rgba(28,26,21,0.6)',
            }}
          >
            Taking on clients, May 2026
          </div>
        </div>
        <div
          style={{
            marginTop: 48,
            flex: 1,
            display: 'grid',
            gridTemplateColumns: '1.4fr 1fr',
            gap: 48,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div
              style={{
                fontFamily: 'EB Garamond, serif',
                fontSize: 108,
                lineHeight: 0.92,
                letterSpacing: '-0.035em',
                fontWeight: 400,
              }}
            >
              Pipeline, <span style={{ fontStyle: 'italic' }}>not</span>
              <br />
              promises.
            </div>
            <div
              style={{
                marginTop: 'auto',
                maxWidth: 460,
                fontSize: 18,
                lineHeight: 1.4,
              }}
            >
              Fractional GTM for B2B companies at $1 to 10M ARR. I bring the playbook, the
              team, and the numbers.
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <PortraitPlaceholder label="THEO LAURENT / PORTRAIT" aspect="4/5" />
            <div
              style={{
                background: '#1C1A15',
                color: '#E9E4DB',
                padding: 20,
                borderRadius: 2,
              }}
            >
              <div
                style={{
                  fontFamily: 'EB Garamond, serif',
                  fontSize: 22,
                  lineHeight: 1.3,
                  fontStyle: 'italic',
                }}
              >
                &quot;Tripled qualified pipeline in one quarter.&quot;
              </div>
              <div
                style={{
                  marginTop: 14,
                  fontSize: 12,
                  fontFamily: 'var(--mono)',
                  letterSpacing: '0.06em',
                  color: 'rgba(233,228,219,0.6)',
                }}
              >
                NORTHWIND · SERIES B
              </div>
            </div>
          </div>
        </div>
      </div>
    </ScaledPreview>
  )
}

function PreviewAmelia() {
  const cases = [
    { co: 'Parcel', tag: 'Series B → C', body: 'Re-architected exec cadence; quarterly offsites.' },
    { co: 'Northwind', tag: 'Post-acquisition', body: 'Integrated two leadership teams in 90 days.' },
    { co: 'Meridian', tag: 'Pre-A', body: 'Built the first board deck; closed the round.' },
    { co: 'Helix Bio', tag: 'Series A', body: 'Org design and hiring plan across 3 functions.' },
    { co: 'Foundry', tag: 'Seed → A', body: 'OKR system, weekly ops review.' },
    { co: 'Cove', tag: 'Series C', body: 'Exec team calibration and succession memo.' },
  ]
  return (
    <ScaledPreview bg="#FAFAF7">
      <div
        style={{
          padding: '36px 56px',
          fontFamily: 'var(--sans)',
          color: '#0A0A0A',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 14,
            alignItems: 'center',
          }}
        >
          <span style={{ fontWeight: 500 }}>
            Amelia Voss, Chief of Staff. Fractional.
          </span>
          <span style={{ color: 'rgba(10,10,10,0.55)' }}>hello@avoss.co</span>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1.6fr',
            gap: 36,
            marginTop: 36,
            flex: 1,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <PortraitPlaceholder label="AMELIA VOSS / PORTRAIT" aspect="4/5" />
            <div
              style={{
                fontSize: 28,
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
                fontWeight: 400,
              }}
            >
              Five years running operating rhythms for founders who don&apos;t have a
              full-time CoS yet.
            </div>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 14,
              alignContent: 'start',
            }}
          >
            {cases.map((c) => (
              <div
                key={c.co}
                style={{
                  border: '1px solid rgba(10,10,10,0.1)',
                  padding: 16,
                  background: '#fff',
                  minHeight: 130,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 13,
                  }}
                >
                  <span style={{ fontWeight: 500 }}>{c.co}</span>
                  <span
                    style={{
                      color: 'rgba(10,10,10,0.5)',
                      fontFamily: 'var(--mono)',
                      fontSize: 11,
                    }}
                  >
                    {c.tag}
                  </span>
                </div>
                <div
                  style={{
                    marginTop: 'auto',
                    fontSize: 14,
                    lineHeight: 1.4,
                    color: 'rgba(10,10,10,0.75)',
                  }}
                >
                  {c.body}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </ScaledPreview>
  )
}

function PreviewKenji() {
  return (
    <ScaledPreview bg="#0F1012">
      <div
        style={{
          padding: '36px 56px',
          fontFamily: 'var(--sans)',
          color: '#E8E8E3',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div
            style={{ fontFamily: 'var(--mono)', fontSize: 14, letterSpacing: '0.04em' }}
          >
            kenji.oba, fractional CTO
          </div>
          <div
            style={{
              display: 'flex',
              gap: 24,
              fontSize: 13,
              color: 'rgba(232,232,227,0.55)',
              fontFamily: 'var(--mono)',
            }}
          >
            <span>/work</span>
            <span>/notes</span>
            <span>/contact</span>
          </div>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 0.7fr',
            gap: 48,
            marginTop: 72,
            flex: 1,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 60,
                lineHeight: 1.05,
                letterSpacing: '-0.02em',
                fontWeight: 400,
              }}
            >
              Senior engineering judgment, without the full-time CTO hire.
            </div>
            <div
              style={{
                marginTop: 48,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 24,
              }}
            >
              <div>
                <div
                  style={{
                    fontFamily: 'var(--mono)',
                    fontSize: 11,
                    color: 'rgba(232,232,227,0.45)',
                    letterSpacing: '0.1em',
                    marginBottom: 10,
                  }}
                >
                  ENGAGEMENT
                </div>
                <div style={{ fontSize: 15, lineHeight: 1.5 }}>
                  Two days a week. Pre-seed through Series A.
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontFamily: 'var(--mono)',
                    fontSize: 11,
                    color: 'rgba(232,232,227,0.45)',
                    letterSpacing: '0.1em',
                    marginBottom: 10,
                  }}
                >
                  COVERS
                </div>
                <div style={{ fontSize: 15, lineHeight: 1.5 }}>
                  Architecture, hiring, board tech memos.
                </div>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PortraitPlaceholder label="KENJI OBA / PORTRAIT" tone="dark" aspect="4/5" />
            <div
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 12,
                lineHeight: 1.85,
                color: 'rgba(232,232,227,0.65)',
              }}
            >
              <div>Stripe · staff eng · 2018–2022</div>
              <div>Figma · founding backend · 2015–2018</div>
              <div>MIT · CSAIL · 2013–2015</div>
            </div>
          </div>
        </div>
        <div
          style={{
            marginTop: 'auto',
            paddingTop: 20,
            borderTop: '1px solid rgba(232,232,227,0.12)',
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 12,
            fontFamily: 'var(--mono)',
            color: 'rgba(232,232,227,0.55)',
          }}
        >
          <span>© 2026</span>
          <span>Engagements from June</span>
        </div>
      </div>
    </ScaledPreview>
  )
}

const GALLERY_ITEMS = [
  { id: 'sarah', name: 'Homepage', role: 'Hero + one-line positioning', Component: PreviewSarah },
  { id: 'marcus', name: 'Proof', role: 'Track record as the hero', Component: PreviewMarcus },
  { id: 'priya', name: 'Services', role: 'Scoped engagements, timelines', Component: PreviewPriya },
  { id: 'theo', name: 'Brand', role: 'Tagline hero + social proof', Component: PreviewTheo },
  { id: 'amelia', name: 'Case studies', role: 'Past engagements in a grid', Component: PreviewAmelia },
  { id: 'kenji', name: 'About', role: 'Credentials and availability', Component: PreviewKenji },
] as const

const SHIPPED_ITEMS = [
  {
    id: 'glutenvrij',
    num: '01',
    name: 'glutenvrijadvies.nl',
    role: 'Editorial content site',
    note: 'Health editorial, Dutch market.',
    url: 'https://glutenvrijadvies.nl',
    Component: PreviewGlutenvrij,
  },
  {
    id: 'brandreel',
    num: '02',
    name: 'Brandreel',
    role: 'Brand showcase',
    note: 'Creative agency showreel.',
    url: 'https://brandreel.com',
    Component: PreviewBrandreel,
  },
] as const

type ShippedItem = (typeof SHIPPED_ITEMS)[number]

function ShippedTile({ item }: { item: ShippedItem }) {
  const { Component, num, name, role, note, url } = item
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={{ display: 'block', color: 'inherit', textDecoration: 'none' }}
    >
      <div style={{ boxShadow: '0 16px 40px -20px rgba(10,10,10,0.14)' }}>
        <Component />
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginTop: 18,
          gap: 16,
        }}
      >
        <div>
          <div
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 11,
              color: 'var(--muted)',
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              marginBottom: 8,
            }}
          >
            {num} / Shipped
          </div>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 17,
              fontWeight: 500,
              letterSpacing: '-0.015em',
              borderBottom: '1px solid var(--line-strong)',
              paddingBottom: 2,
            }}
          >
            {name}
            <span aria-hidden="true" style={{ fontSize: 13 }}>
              ↗
            </span>
          </div>
          <div
            style={{
              fontSize: 14,
              color: 'var(--muted)',
              marginTop: 6,
            }}
          >
            {note}
          </div>
        </div>
        <span
          style={{
            fontSize: 13,
            color: 'var(--muted)',
            fontFamily: 'var(--mono)',
            whiteSpace: 'nowrap',
            paddingTop: 22,
          }}
        >
          {role}
        </span>
      </div>
    </a>
  )
}

export function ShippedGrid() {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 1fr)',
        gap: 40,
      }}
    >
      <ShippedTile item={SHIPPED_ITEMS[0]} />
      <div style={{ marginTop: 96 }}>
        <ShippedTile item={SHIPPED_ITEMS[1]} />
      </div>
    </div>
  )
}

type GalleryItem = (typeof GALLERY_ITEMS)[number]

function GalleryTile({ item }: { item: GalleryItem }) {
  const { Component, name, role } = item
  return (
    <div style={{ width: TILE_WIDTH, flexShrink: 0 }}>
      <div
        style={{
          boxShadow: '0 8px 24px -12px rgba(10,10,10,0.08)',
        }}
      >
        <Component />
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginTop: 14,
          gap: 12,
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 500, letterSpacing: '-0.01em' }}>
          {name}
        </span>
        <span
          style={{
            fontSize: 13,
            color: 'var(--muted)',
            fontFamily: 'var(--mono)',
            fontWeight: 400,
          }}
        >
          {role}
        </span>
      </div>
    </div>
  )
}

function MarqueeRow({
  items,
  direction,
  durationSeconds,
}: {
  items: ReadonlyArray<GalleryItem>
  direction: 'left' | 'right'
  durationSeconds: number
}) {
  const loop = [...items, ...items]
  return (
    <div style={{ overflow: 'hidden', width: '100%' }}>
      <div
        className="marquee-track"
        style={{
          display: 'flex',
          gap: GAP,
          width: 'max-content',
          animation: `marquee-scroll ${durationSeconds}s linear infinite`,
          animationDirection: direction === 'left' ? 'normal' : 'reverse',
          willChange: 'transform',
        }}
      >
        {loop.map((item, i) => (
          <GalleryTile key={`${item.id}-${i}`} item={item} />
        ))}
      </div>
    </div>
  )
}

export function Gallery() {
  const half = Math.ceil(GALLERY_ITEMS.length / 2)
  const rowA = GALLERY_ITEMS.slice(0, half)
  const rowB = GALLERY_ITEMS.slice(half)
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 40,
        marginLeft: 'calc(50% - 50vw)',
        marginRight: 'calc(50% - 50vw)',
      }}
    >
      <MarqueeRow items={rowA} direction="left" durationSeconds={70} />
      <MarqueeRow items={rowB} direction="right" durationSeconds={80} />
    </div>
  )
}
